import { makeStyles } from '@material-ui/core/styles';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { SizeType } from 'src/common/enumerations/cashier-preference-level';
import CashierPreferencesHelper from 'src/common/helpers/cashier-preferences-helper';
import { px, rem, vh, vw } from 'src/common/helpers/dimension-helper';
import { themeColor } from 'src/mui-theme';

export interface ActionWindowStylesProps {
  isLeftHanded?: boolean;
  isKeypadVisible?: boolean;
  buttonSize?: SizeType;
  textSize?: SizeType;
}

const linkedLineMargin = '3.25vh';

const detailsFontSize: CSSProperties = {
  fontSize: rem(1),
  '@media (width: 1024px) and (height: 576px)': {
    fontSize: rem(0.875),
  },
  '@media (width: 1600px) and (height: 900px)': {
    fontSize: rem(1.125),
  },
  '@media (width: 1600px) and (height: 1200px)': {
    fontSize: rem(1.375),
  },
  '@media (width: 1920px) and (height: 1080px)': {
    fontSize: rem(1.5),
  },
};

const detailsPriceUnitFontSize: CSSProperties = {
  fontSize: rem(1.125),
  '@media (width: 1024px) and (height: 576px)': {
    fontSize: rem(1),
  },
  '@media (width: 1600px) and (height: 900px)': {
    fontSize: rem(1.25),
  },
  '@media (width: 1600px) and (height: 1200px)': {
    fontSize: rem(1.5),
  },
  '@media (width: 1920px) and (height: 1080px)': {
    fontSize: rem(1.625),
  },
};

const handleButtonSize = (size: number = 1): string => {
  return `${CashierPreferencesHelper.sizeMultiplier(size) * 8.84}vw`;
};

export const styles = makeStyles(theme => ({
  contentContainer: ({ isLeftHanded }: ActionWindowStylesProps) => ({
    flex: '1 1 0',
    padding: '4.25vh 1.30vw 1.85vh 0.65vw',
    '@media (min-width: 1024px) and (min-height: 576px)': {
      padding: '1vh 1.30vw 1.3vh 0.65vw',
      paddingLeft: isLeftHanded ? '3.16vw' : '1.30vw',
      paddingRight: !isLeftHanded ? '3.16vw' : '1.30vw',
    },
    paddingLeft: isLeftHanded ? '3.16vw' : '1.30vw',
    paddingRight: !isLeftHanded ? '3.16vw' : '1.30vw',
    zIndex: 1,
  }),
  actionWindowMain: ({ isLeftHanded }: ActionWindowStylesProps) => ({
    paddingLeft: !isLeftHanded ? '1vw' : 0,
    paddingRight: isLeftHanded ? '1vw' : 0,
    flex: '1 1 auto',
  }),
  actionWindowButtons: ({ buttonSize }: ActionWindowStylesProps) => ({
    flex: `0 0 ${handleButtonSize(buttonSize)}`,
    maxWidth: `${handleButtonSize(buttonSize)}`,
  }),
  actionWindowButtonsSmallForm: ({ buttonSize }: ActionWindowStylesProps) => ({
    flex: `0 0 ${handleButtonSize(buttonSize)}`,
  }),
  autoLeftMargin: {
    marginLeft: 'auto',
  },
  addLeftPadding: {
    paddingLeft: '1vw',
  },
  flexStart: {
    alignContent: 'flex-start',
  },
  positionRelative: {
    position: 'relative',
  },
  textAlignRight: {
    textAlign: 'right',
  },
  superText: {
    '@media (max-width: 1024px)': { verticalAlign: 'super' },
    verticalAlign: 'top',
  },
  idLine: {
    marginLeft: '3.1vw',
    color: themeColor.subheaderLight,
    wordBreak: 'break-word',
  },
  idLineMarginSmall: () => ({
    '@media (width: 1024px) and (height: 768px)': {
      marginLeft: '2.5vw',
    },
    '@media (width: 1366px) and (height: 768px)': {
      marginLeft: '2vw',
    },
  }),
  eligibility: {
    marginTop: '1.45vh',
    marginBottom: '1.45vh',
  },
  eligibilityContainer: {
    marginTop: '1.2vh',
  },
  itemContainer: {
    marginTop: '4.69vh',
  },
  marginTop1Vh: {
    marginTop: '1vh',
  },
  icon: {
    marginRight: '0.5vw',
    color: theme.palette.secondary.main,
    fill: theme.palette.secondary.main,
    height: '2.34vh',
    width: 'auto',
  },
  iconMinWidth: {
    minWidth: '20px',
  },
  linkedLineIcon: {
    display: 'flex',
    width: linkedLineMargin,
  },
  item: {
    ...detailsPriceUnitFontSize,
    height: 'fit-content',
    fontWeight: 500,
    letterSpacing: '0.25px',
    width: '100%',
    paddingTop: '1.5vh',
    display: 'flex',
    alignItems: 'center',
  },
  itemPrice: {
    height: 'fit-content',
    fontSize: rem(1.25),
    '@media (width: 1024px) and (height: 576px)': {
      fontSize: rem(1.125),
    },
    '@media (width: 1600px) and (height: 900px)': {
      fontSize: rem(1.375),
    },
    '@media (width: 1600px) and (height: 1200px)': {
      fontSize: rem(1.625),
    },
    '@media (width: 1920px) and (height: 1080px)': {
      fontSize: rem(1.75),
    },
    fontWeight: 500,
    letterSpacing: '0.25px',
    width: '100%',
    paddingTop: '1.5vh',
  },
  itemId: {
    fontSize: vh(2),
  },
  paddingTop: {
    paddingTop: '15px',
  },
  quantityLabel: {
    fontSize: rem(1.125),
    fontWeight: 500,
    marginRight: '0.33vw',
  },
  promotionDescriptionItem: {
    flex: '1 0',
  },
  promotionPriceItem: {
    marginLeft: 'auto',
    flex: '0 1 auto',
    textAlign: 'right',
  },
  MSUContainer: {
    width: '30%',
  },
  overrideLabel: {
    ...detailsFontSize,
    fontWeight: 500,
  },
  keypadGridItem: ({ isLeftHanded, isKeypadVisible }: ActionWindowStylesProps) => ({
    flex: '0 0 auto',
    paddingRight: isKeypadVisible && !isLeftHanded ? '1.06vw' : 0,
    paddingLeft: isKeypadVisible && isLeftHanded ? '1.06vw' : 0,
    paddingBottom: '1.82vh',
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    zIndex: 1,
  }),
  footer: {
    marginTop: 'auto',
  },
  pushToBottom: {
    position: 'absolute',
    bottom: 0,
    minHeight: '8vh',
  },
  taxMinHeight: {
    minHeight: '12vh',
  },
  priceOverride: {
    textAlign: 'right',
  },
  priceOverrideText: {
    color: themeColor.black,
  },
  strikeThrough: {
    textDecoration: 'line-through 2px',
    textDecorationColor: themeColor.alert,
  },
  price: {
    ...detailsPriceUnitFontSize,
    fontWeight: 500,
  },
  tax: {
    fontWeight: 500,
    color: themeColor.subheaderLight,
    paddingTop: '4vh',
    fontSize: vh(2),
  },
  fitHeight: {
    height: 'fit-content',
  },
  labelText: {
    fontWeight: 500,
    letterSpacing: '0.1vw',
    fontSize: vh(1.6),
    '@media (max-width: 1024px) and (max-height: 576px)': {
      fontSize: vh(1.45),
    },
    lineHeight: '2.08vh',
  },
  descriptionContainer: {
    overflow: 'auto',
    paddingTop: '2.5vh',
  },
  linkedDescriptionContainer: {
    overflow: 'auto',
    marginLeft: linkedLineMargin,
    marginTop: '2vh',
  },
  linkedLine: {
    ...detailsFontSize,
    fontWeight: 500,
    lineHeight: '2.2vh',
    color: themeColor.modalTitle,
  },
  itemLineBottomMargin: {
    height: 'fit-content',
    marginBottom: '2vh',
  },
  couponLine: {
    fontWeight: 500,
    lineHeight: '4vh',
    fontSize: vh(2),
    letterSpacing: '.02vw',
    color: themeColor.modalTitle,
  },
  code: {
    fontWeight: 500,
    color: themeColor.subheaderLight,
    paddingTop: '15px',
    ...detailsFontSize,
  },
  weightEntered: {
    fontSize: rem(1),
    fontWeight: 500,
    color: themeColor.subheaderLight,
    fontStyle: 'normal',
  },
  lineItem: {
    ...detailsFontSize,
    fontWeight: 500,
    color: themeColor.modalTitle,
  },
  linkedItemPrice: {
    fontSize: rem(1.25),
    '@media (width: 1024px) and (height: 576px)': {
      fontSize: rem(1.125),
    },
    '@media (width: 1600px) and (height: 900px)': {
      fontSize: rem(1.375),
    },
    '@media (width: 1600px) and (height: 1200px)': {
      fontSize: rem(1.625),
    },
    '@media (width: 1920px) and (height: 1080px)': {
      fontSize: rem(1.75),
    },
    fontWeight: 500,
    color: themeColor.subheaderLight,
  },
  linkedLinePrice: {
    ...detailsPriceUnitFontSize,
  },
  UOMText: {
    ...detailsFontSize,
    fontWeight: 500,
    color: themeColor.subheaderLight,
  },
  autoTopMargin: {
    marginTop: 'auto',
    height: '5vh',
  },
  containerStyle: {
    paddingLeft: '0.5vh',
  },
  ItemDescriptionPadding: {
    paddingTop: '1vh',
  },
  perUnitText: {
    ...detailsPriceUnitFontSize,
    color: theme.palette.info.dark,
    fontWeight: 500,
  },
  itemVoidedLabel: {
    fontSize: vh(2),
    fontWeight: 500,
    marginLeft: '0.73vw',
  },
  itemVoided: {
    textAlign: 'right',
  },
  itemVoidedText: {
    color: themeColor.black,
  },
  voidedCount: {
    fontSize: vh(2),
    fontWeight: 500,
  },
  productVoidedText: {
    marginTop: '1.5vh',
    fontSize: vh(3),
    fontWeight: 500,
    textAlign: 'center',
  },
  productVoidedImage: {
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: '1.9vh',
  },
  smallText: {
    '@media (max-width: 1600px)': {
      '& $icon': {
        width: '2.6vw',
        height: '3.43vh',
      },
    },
  },
  ItemDescriptionHeight: {
    height: 'fit-content',
  },
  linkedLineContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  trainingWaterMark: {
    left: '12%',
  },
  customContainerHeight: ({ textSize }: ActionWindowStylesProps) => ({
    maxHeight: textSize === 1 ? '48vh' : textSize === 2 ? '41vh' : '49vh',
    '@media (width: 1024px) and (height: 576px)': {
      maxHeight: textSize === 1 ? '44vh' : textSize === 2 ? '44vh' : '44vh',
    },
    '@media (width: 1024px) and (height: 768px)': {
      maxHeight: textSize === 1 ? '47vh' : textSize === 2 ? '41vh' : '48vh',
    },
    '@media (width: 1280px) and (height: 720px)': {
      maxHeight: textSize === 1 ? '47vh' : textSize === 2 ? '49vh' : '48vh',
    },
    '@media (width: 1366px) and (height: 768px)': {
      maxHeight: textSize === 1 ? '48vh' : textSize === 2 ? '41vh' : '49vh',
    },
    '@media (width: 1920px) and (height: 1080px)': {
      maxHeight: textSize === 1 ? '46vh' : textSize === 2 ? '46vh' : '50vh',
    },
  }),
  loyaltyCustomerLine: {
    color: themeColor.defaultTextColor,
  },
  loyaltyCustomerName: {
    marginLeft: vw(0.73),
    fontSize: rem(1.125),
  },
  loyaltyCustomerAndCardWrapper: {
    gap: vh(1.56),
    paddingTop: vh(6.771),
  },
  loyaltyTotalPointsLine: {
    color: themeColor.defaultTextColor,
    fontSize: rem(1),
  },
  loyaltyTotalPoints: {
    marginLeft: vw(0.73),
    minWidth: vw(4.69),
    textAlign: 'right',
  },
  scrollWrapper: {
    height: '22.79vh',
    marginTop: '1vh',
    flexGrow: 1,
    flexShrink: 1,
  },
  indentedLine: {
    paddingLeft: `calc(${px(24)} + ${vw(0.73)})`,
    justifyContent: 'space-between',
  },
  indCardHolder: {
    color: themeColor.defaultTextColor,
    marginBottom: vh(6.25),
    columnGap: vw(1.32),
    flexWrap: 'nowrap',
    fontSize: rem(1),
    '& span:last-child': {
      minWidth: 'fit-content',
    },
  },
  alignCenter: {
    alignSelf: 'center',
  },
  taxExemptionContainer: {
    paddingTop: '1.2vh',
  },
  taxExemptionLine: {
    fontWeight: 500,
    lineHeight: '3vh',
    fontSize: rem(1),
    letterSpacing: '.02vw',
    color: themeColor.subheaderLight,
  },
  taxExemptionScrollContainer: {
    maxHeight: ({ textSize }: ActionWindowStylesProps) => {
      switch (textSize) {
        case SizeType.Large:
          return '50vh';
        case SizeType.Medium:
        default:
          return '56vh';
      }
    },
  },
  totalContanier: {
    justifyContent: 'flex-end',
  },
  keypadMaxWidth: {
    maxWidth: '25vw',
  },
  subContainerMaxWidth: {
    maxWidth: '25.8vw',
  },
  actionWindowText: { textOverflow: 'ellipsis', overflow: 'hidden' },

  itemFeeLine: {
    fontWeight: 500,
    color: themeColor.subheaderLight,
    ...detailsFontSize,
  },

  groupFeeLine: {
    marginBottom: '1.5vh',
    fontWeight: 500,
    color: themeColor.subheaderLight,
    ...detailsFontSize,
  },
  itemFeeContainer: {
    marginTop: '1.2vh',
  },
  maxWidthSmallForm: {
    maxWidth: '42vw',
  },
}));
